import type { Timestamps, SoftDeletable, PaginationParams, DateRangeFilter } from "./common";

// ─── Enums ────────────────────────────────────────────────────────────────────

export type CalendarEventType =
  | "pedido"
  | "entrega"
  | "pago"
  | "reunion"
  | "mantenimiento"
  | "otro";

export type CalendarEventStatus =
  | "pendiente"
  | "completado"
  | "cancelado";

export type CalendarRecurrence = "none" | "weekly" | "monthly" | "yearly";

export const RECURRENCE_LABELS: Record<CalendarRecurrence, string> = {
  none: "No se repite",
  weekly: "Cada semana",
  monthly: "Cada mes",
  yearly: "Cada año",
};

// ─── Labels (for UI display) ──────────────────────────────────────────────────

export const EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  pedido:        "Pedido",
  entrega:       "Entrega",
  pago:          "Pago",
  reunion:       "Reunión",
  mantenimiento: "Mantenimiento",
  otro:          "Otro",
};

export const EVENT_STATUS_LABELS: Record<CalendarEventStatus, string> = {
  pendiente:  "Pendiente",
  completado: "Completado",
  cancelado:  "Cancelado",
};

/**
 * Tailwind color classes per event type.
 * Format: { bg, text, darkBg, darkText } for badge rendering.
 */
export const EVENT_TYPE_COLORS: Record<CalendarEventType, string> = {
  pedido:        "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400",
  entrega:       "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  pago:          "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  reunion:       "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  mantenimiento: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  otro:          "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-400",
};

export const EVENT_STATUS_COLORS: Record<CalendarEventStatus, string> = {
  pendiente:  "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  completado: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  cancelado:  "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

export const EVENT_TYPE_VALUES = Object.keys(EVENT_TYPE_LABELS) as CalendarEventType[];

// ─── Calendar Event ───────────────────────────────────────────────────────────

export interface CalendarEvent extends Timestamps, SoftDeletable {
  id:              number;
  titulo:          string;
  descripcion:     string | null;
  fecha:           string;          // ISO date YYYY-MM-DD
  hora_inicio:     string | null;   // "HH:MM"
  hora_fin:        string | null;   // "HH:MM"
  tipo:            CalendarEventType;
  status:          CalendarEventStatus;
  referencia_id:   number | null;   // optional link to pedido or other entity
  referencia_tipo: string | null;
  color:           string | null;   // hex override per event (optional)
  todo_el_dia:     boolean;         // was: 1 | 0 — corrected to boolean
  
    recurrencia: CalendarRecurrence;

  recurrencia_fin:
    string | null;

  recordatorio_email:
    boolean;

  recordatorio_anticipacion:
    number | null;

  recordatorio_enviado_at:
    string | null;
}

// ─── Request types ────────────────────────────────────────────────────────────

export interface CalendarEventCreate {
  titulo:           string;
  descripcion?:     string;
  fecha:            string;
  hora_inicio?:     string;
  hora_fin?:        string;
  tipo:             CalendarEventType;
  status?:          CalendarEventStatus;
  referencia_id?:   number;         // optional — no automations in Phase 7
  referencia_tipo?: string;
  color?:           string;
  todo_el_dia?:     boolean;
  
    recurrencia?:
    CalendarRecurrence;

  recurrencia_fin?:
    string;

  recordatorio_email?:
    boolean;

  recordatorio_anticipacion?:
    number;
}

export interface CalendarEventUpdate extends Partial<CalendarEventCreate> {}

export interface CalendarFilters extends PaginationParams, DateRangeFilter {
  tipo?:   CalendarEventType;
  status?: CalendarEventStatus;
}

// ─── Dashboard upcoming events shape ─────────────────────────────────────────

export interface ProximoEvento {
  id:          number;
  titulo:      string;
  fecha:       string;
  hora_inicio: string | null;
  tipo:        CalendarEventType;
  status:      CalendarEventStatus;
}

